import { ComponentType } from '@angular/cdk/overlay';
import { NgComponentOutlet } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FeatureFlagId } from '@api-types/enums/feature-flag-id.enum';
import { selectFeatureFlagById } from '@core/store/feature-flags/feature-flags.selectors';
import { PmTab, PmTabSwitchComponent } from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { AnalysisPageComponent } from 'demand-forecasting/analysis/pages/analysis-page.component';
import { ForecastVersionStateHandlerComponent } from 'demand-forecasting/page-states/forecast-version-state-handler.component';
import { ReportingPageComponent } from 'demand-forecasting/reporting/pages/reporting-page.component';
import { FilterActions } from 'demand-forecasting/store/filter/filter.actions';
import { ForecastSeriesActions } from 'demand-forecasting/store/forecast-series/forecast-series.actions';
import { ForecastVersionActions } from 'demand-forecasting/store/forecast-version/forecast-version.actions';
import { ForecastTab } from 'demand-forecasting/types/forecast-tab.enum';
import { InsightsPageComponent } from 'insights/pages/insights-page.component';

export const DEMAND_PAGE_TAB_QUERY_KEY = 'demandTab';

@Component({
  selector: 'pm-forecast-detail-page',
  templateUrl: './forecast-detail-page.component.html',
  host: { class: 'flex flex-col h-full' },
  imports: [
    ForecastVersionStateHandlerComponent,
    TranslateModule,
    PmTabSwitchComponent,
    NgComponentOutlet,
  ],
})
export class ForecastDetailPageComponent implements OnInit, OnDestroy {
  protected ForecastTab = ForecastTab;
  private readonly store = inject(Store);

  protected DEMAND_PAGE_TAB_QUERY_KEY = DEMAND_PAGE_TAB_QUERY_KEY;

  protected selectedTabId = signal<ForecastTab | null>(null);

  private readonly insightsFlag = this.store.selectSignal(
    selectFeatureFlagById(FeatureFlagId.insights),
  );
  private readonly analysisPageFlag = this.store.selectSignal(
    selectFeatureFlagById(FeatureFlagId.analysis_page),
  );
  private readonly reportingPageFlag = this.store.selectSignal(
    selectFeatureFlagById(FeatureFlagId.reporting_page),
  );

  protected readonly TAB_TO_PAGE_COMPONTENT_MAPPING: Record<
    ForecastTab,
    ComponentType<unknown>
  > = {
    FORECAST: ForecastVersionStateHandlerComponent,
    INSIGHTS: InsightsPageComponent,
    ANALYSIS: AnalysisPageComponent,
    REPORTING: ReportingPageComponent,
  };

  protected readonly availableTabs = computed(() => {
    const tabs: PmTab<ForecastTab>[] = [
      {
        labelKey: 'RESULT.TAB.FORECAST',
        id: ForecastTab.FORECAST,
      },
    ];

    const insightsEnabled = this.insightsFlag().enabled;
    if (insightsEnabled) {
      tabs.push({
        labelKey: 'RESULT.INSIGHTS.TAB_LABEL',
        id: ForecastTab.INSIGHTS,
      });
    }

    const analysisEnabled = this.analysisPageFlag().enabled;
    if (analysisEnabled) {
      tabs.push({
        labelKey: 'ANALYSIS.TAB',
        id: ForecastTab.ANALYSIS,
      });
    }

    const reportingEnabled = this.reportingPageFlag().enabled;
    if (reportingEnabled) {
      tabs.push({
        labelKey: 'REPORTING.TAB',
        id: ForecastTab.REPORTING,
      });
    }

    return tabs;
  });

  ngOnInit(): void {
    this.store.dispatch(FilterActions.getSavedFilterCombinations());
  }

  // TODO discuss if this is the best place to reset the state or if it should be done in the router effects
  ngOnDestroy(): void {
    this.store.dispatch(ForecastSeriesActions.resetForecastSeries());
    this.store.dispatch(ForecastVersionActions.resetForecastVersion());
    this.store.dispatch(FilterActions.resetFilters());
  }
}
